import { z } from 'zod';
import { Strings } from '../constants/Strings';

// Get validation strings
const getValidationStrings = () => Strings.get().events.validation;

// Define the group option type for the dropdown
const groupOptionSchema = z.object({
  value: z.string(),
  label: z.string(),
});

// Image can be a browser File (web) or a legacy object with uri/width/height/mime, or null
const imageFileSchema = z.any().optional().nullable();

export const createGroupEventSchema = () => {
  const v = getValidationStrings();
  return z
    .object({
      title: z
        .string()
        .trim()
        .min(1, v.title.required)
        .max(100, v.title.tooLong),

      startTime: z.date({
        required_error: v.startTime.required,
        invalid_type_error: v.startTime.invalid,
      }).nullable(),

      locationString: z
        .string()
        .trim()
        .min(1, v.location.required)
        .max(250, v.location.tooLong),

      description: z
        .string()
        .trim()
        .min(20, v.description.tooShort)
        .min(1, v.description.required),

      whatToBring: z.string().trim().optional(),

      maxEventAttendees: z
        .string()
        .trim()
        .min(1, v.maxEventAttendees.required)
        .refine((val) => !isNaN(Number(val)), {
          message: v.maxEventAttendees.mustBeNumber,
        })
        .refine((val) => Number.isInteger(Number(val)), {
          message: v.maxEventAttendees.mustBeInteger,
        })
        .refine((val) => Number(val) >= 1, {
          message: v.maxEventAttendees.minimum,
        })
        .refine((val) => Number(val) <= 10000, {
          message: v.maxEventAttendees.maximum,
        }),

      maxGuests: z
        .string()
        .trim()
        .min(1, v.maxGuests.required)
        .refine((val) => !isNaN(Number(val)), {
          message: v.maxGuests.mustBeNumber,
        })
        .refine((val) => Number.isInteger(Number(val)), {
          message: v.maxGuests.mustBeInteger,
        })
        .refine((val) => Number(val) >= 0, {
          message: v.maxGuests.minimum,
        })
        .refine((val) => Number(val) <= 1000, {
          message: v.maxGuests.maximum,
        }),

      group: groupOptionSchema.nullable(),

      imageFile: imageFileSchema,
    })
    .refine((data) => Number(data.maxGuests) < Number(data.maxEventAttendees), {
      message: v.maxGuests.mustBeLessThanAttendees,
      path: ['maxGuests'],
    });
};

export type GroupEventFormValues = z.infer<ReturnType<typeof createGroupEventSchema>>;

export type GroupOption = z.infer<typeof groupOptionSchema>;

export type ImageFile = z.infer<typeof imageFileSchema>;

// Default values for the form
export const groupEventDefaultValues: GroupEventFormValues = {
  title: '',
  startTime: null,
  locationString: '',
  description: '',
  whatToBring: '',
  maxEventAttendees: '',
  maxGuests: '',
  group: null,
  imageFile: null,
};